import { Formik } from "formik";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { settingsFormApi } from "../../api/setting";
import Loader from "../../component/Loader";

function SettingsForm() {
  const [loading, setLoading] = useState(false);
  const [fetchingFee, setFetchingFee] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companyBookingFee, setCompanyBookingFee] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  
  const authState = useSelector((store) => store.auth);
  const isSuperAdmin = authState?.user_type === 'super_admin';
  
  let companyOptions = [];
  const companiesStr = localStorage.getItem('companies');
  
  if (companiesStr) {
    try {
      const companies = JSON.parse(companiesStr);
      
      if (Array.isArray(companies) && companies.length > 0) {
        companyOptions = [
          { value: '', label: 'Select Company' },
          ...companies.map(c => ({
            value: c.id,
            label: c.company_name
          }))
        ];
      }
    } catch (e) {
      companyOptions = [];
    }
  }
  
  // Ensure companyOptions has at least the default option for super admin
  if (isSuperAdmin && companyOptions.length === 0) {
    companyOptions = [{ value: '', label: 'Select Company' }];
  }

  // For company admins, show form directly
  useEffect(() => {
    if (!isSuperAdmin) {
      setShowBookingForm(true);
      // Set initial booking fee from localStorage for company admins
      setCompanyBookingFee(localStorage.getItem('booking_fee') || '');
    }
  }, [isSuperAdmin]);

  // Fetch company booking fee
  const fetchCompanyBookingFee = async (companyId) => {
    setFetchingFee(true);
    try {
      const response = await settingsFormApi({ company_id: companyId, get_booking_fee: true });
      if (response?.code === 1 && response?.data?.booking_fee) {
        setCompanyBookingFee(response.data.booking_fee);
      } else {
        setCompanyBookingFee(''); // Default to empty if no booking fee found
      }
    } catch (error) {
      console.error('Error fetching company booking fee:', error);
      setCompanyBookingFee('');
    } finally {
      setFetchingFee(false);
    }
  };

  // Handle company selection for super admin
  const handleCompanyChange = async (e) => {
    const companyId = e.target.value;
    setSelectedCompany(companyId);
    
    if (companyId) {
      setShowBookingForm(true);
      // Fetch booking fee for selected company
      await fetchCompanyBookingFee(companyId);
    } else {
      setShowBookingForm(false);
      setCompanyBookingFee('');
    }
  };
  return (
    <article className="col-lg-9">
      <div className="article-body store-bd-gy">
        <div className="row mb-4">
          <div className="col-md-12">
            <h4>Settings</h4>
          </div>
        </div>
        
        {/* Company Selection - Only show if there are company options */}
        {isSuperAdmin && companyOptions.length > 0 && (
          <div className="row mb-4">
            <div className="col-md-6">
              <label htmlFor="company_id" className="form-label">Select Company</label>
              <select
                className="form-control"
                name="company_id"
                value={selectedCompany}
                onChange={handleCompanyChange}
                disabled={fetchingFee}
              >
                {companyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {fetchingFee && (
                <small className="text-muted">Fetching booking fee...</small>
              )}
            </div>
          </div>
        )}
        
        {/* Note for Super Admin */}
        {isSuperAdmin && !selectedCompany && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-info" role="alert">
                <i className="fas fa-info-circle me-2"></i>
                Please select a company to view and edit settings.
              </div>
            </div>
          </div>
        )}
          
          {/* Booking Fee Form - Show only when company is selected (for super admin) or always for company admin */}
          {showBookingForm && (
            <div className="row">
              <div className="col-12">
                <div className="card rounded-lg">
                  <div className="card-body">
                    <h5 className="mb-4">Booking Fee Settings</h5>
                    <Formik
                      initialValues={{
                        booking_fee: companyBookingFee || localStorage.getItem('booking_fee'),
                        company_id: selectedCompany,
                      }}
                      enableReinitialize={true}
                      
                      onSubmit={(values, { isSubmitting, resetForm }) => {
                        setLoading(true);
                        const payload = {
                          booking_fee: values.booking_fee,
                        };
                        
                        // Add company_id for super admin
                        if (isSuperAdmin && selectedCompany) {
                          payload.company_id = selectedCompany;
                        }
                        
                        settingsFormApi(payload).then((res) => {
                          setLoading(false);

                          if (res?.code === 1) {
                            toast.success(res?.message, {  autoClose: false,  closeButton: true});
                            localStorage.setItem("booking_fee",values.booking_fee);
                          } else {
                            toast.error(res?.message, {  autoClose: false,  closeButton: true});
                          }
                        });
                      }}
                    >
                      {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                        setFormikState,
                      }) => (
                        <form onSubmit={handleSubmit}>
                          <div className="row">
                            <div className="col-md-6">
                              <label htmlFor="booking_fee" className="form-label">Booking Fee</label>
                              <div className="input-container">
                                <input
                                  type="number"
                                  className="form-control ps-4"
                                  placeholder="Enter booking fee"
                                  name="booking_fee"
                                  value={values.booking_fee}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  disabled={fetchingFee}
                                />
                                <img
                                  src="assets/images/price.png"
                                  className="input-img"
                                  alt=""
                                  style={{ top: '50%', transform: 'translateY(-50%)' }}
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="row mt-4">
                            <div className="col-12">
                              <button
                                type="submit"
                                className="btn btn-save"
                                disabled={loading || fetchingFee || (isSuperAdmin && !selectedCompany)}
                              >
                                {loading ? <Loader /> : "Save"}
                              </button>
                            </div>
                          </div>
                        </form>
                      )}
                    </Formik>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </article>
  );
}

export default SettingsForm;
